import Head from 'next/head';
import { useRouter } from 'next/router';
import { Box, Container, Grid, GridItem, Heading, Text } from '@chakra-ui/react';

import Layout from '../../components/Layout';
import metaRobots from '../../helpers/metaRobots';
import sharecardImage from '../../public/images/sharecard.jpg';
import TerminalDifferenceAnimation from '../../components/Animations/Hero/TerminalDifferenceAnimation';
import MasonryCard from '../../components/UI/MasonryCard';
import SquarePattern from '../../components/UI/SquarePattern';
import iconCode from '../../public/images/hiring-developers/icons/frontend.svg';
import iconCog from '../../public/images/hiring-developers/icons/backend.svg';
import iconStack from '../../public/images/hiring-developers/icons/stack.svg';
import iconList from '../../public/images/hiring-developers/icons/data.svg';
import iconPlusPeople from '../../public/images/hiring-developers/icons/others.svg';
import sitePath from '../../helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const Hero = ({ hero }) => {
  return (
    <Container>
      <Grid
        gridTemplateColumns={{
          lg: '1fr 1fr',
          xl: '6fr 4fr',
        }}
        gap={10}
        alignItems={{ lg: 'center' }}
      >
        <GridItem order={{ lg: 1 }}>
          <TerminalDifferenceAnimation hero={hero} />
        </GridItem>
        <GridItem>
          <Heading
            as="h1"
            mb={{ base: 4, lg: 6 }}
            fontSize={{
              base: '2.5rem',
              xl: '3rem',
            }}
            lineHeight={{ base: 'none' }}
          >
            {hero.title.main}
            {hero.title.highlight && (
              <Text as="span" color={hero.title.color ? hero.title.color : 'green.500'}>
                {hero.title.highlight}
              </Text>
            )}
            {hero.title.secondary}
          </Heading>
          {hero.text && (
            <Box
              lineHeight="shorter"
              fontSize={{ base: 'md', lg: 'lg' }}
              dangerouslySetInnerHTML={{ __html: hero.text }}
              maxW={{ xl: '80%' }}
              sx={{ p: { mb: 4 } }}
            />
          )}
        </GridItem>
      </Grid>
    </Container>
  );
};

const hero = {
  title: {
    main: 'Get instant access to ',
    highlight: '100k',
    secondary: ' seasoned tech professionals',
  },
  text: `<p><strong>Hire pre-vetted pros</strong> from Canada, Europe and Latin America with, on average, nearly a decade of experience specializing in a wide range of programming languages and skills.</p>
        <p><strong>Match with tech stack savants</strong> specializing in a wide range of programming languages and skills.  Which roles are you struggling to fill?</p>`,
  button: {},
  roles: [
    {
      icon: iconCode,
      role: 'Front-End Developer',
      url: '/contact?itm_source=terminal.io&itm_medium=roles_links&itm_campaign=demand_page&itm_content=frontend',
    },
    {
      icon: iconCog,
      role: 'Back-End Developer',
      url: '/contact?itm_source=terminal.io&itm_medium=roles_links&itm_campaign=demand_page&itm_content=backend',
    },
    {
      icon: iconStack,
      role: 'Full-Stack Developer',
      url: '/contact?itm_source=terminal.io&itm_medium=roles_links&itm_campaign=demand_page&itm_content=fullstack',
    },
    {
      icon: iconList,
      role: 'Other Roles',
      url: '/contact?itm_source=terminal.io&itm_medium=roles_links&itm_campaign=demand_page&itm_content=otherroles',
    },
    {
      icon: iconPlusPeople,
      role: 'Multiple Roles',
      url: '/contact?itm_source=terminal.io&itm_medium=roles_links&itm_campaign=demand_page&itm_content=multipleroles',
    },
  ],
};

const footer = {
  longText: false,
  text: 'Find the scale you need. Build your team today.',
  image: '/images/home/businesses-footer.jpg',
  button: {
    url: null,
    text: 'Build My Team',
  },
};

const meta = {
  title: 'The Terminal Difference - Terminal.io',
  description:
    'Get instant access to 100k seasoned tech professionals. Hire pre-vetted pros from Canada, Europe, and Latin America.',
};

const masonryCards = [
  {
    type: 'content_image',
    position: false,
    bgDark: true,
    image: {
      sourceUrl: '/images/home/how-it-works-bg.svg',
      altText: 'Graphic representation of virtual engineering opportunities with Terminal',
    },
    title: 'Build a world-class team from start to finish fast',
    text: [
      {
        strong: 'Spend less time interviewing',
        normal:
          '– provide feedback quickly and fast-track pre-screened candidates with Terminal’s Talent Hub.',
      },
      {
        strong: 'Trust our guarantee',
        normal:
          '– receive 5 qualified candidates per open role within 2 weeks of signing up as a client.',
      },
    ],
    button: [
      {
        type: 'dark',
        text: {
          normal: 'Learn more about the',
          strong: 'Terminal Talent Platform',
        },
        url: '/products/talent-platform',
      },
    ],
    hash: 'world-class-team',
  },
  {
    type: 'content_image',
    position: true,
    bgDark: true,
    image: {
      sourceUrl: '/images/home/home-businesses-administrative-work.jpg',
      altText: 'Businessman holding a pen and smiling in front of a laptop',
    },
    title: 'Shift all risk and administrative work to Terminal',
    text: [
      {
        strong: 'Tap into localized knowledge and benefits',
        normal:
          'including admin, legal red tape and HR compliance, so your business can focus on what really matters.',
      },
      {
        strong: 'Add new members seamlessly',
        normal: '– your remote hires are fully onboarded by Terminal and ready to work.',
      },
    ],
    button: [
      {
        type: 'dark',
        text: {
          normal: 'Learn more about the',
          strong: 'Remote Management Platform',
        },
        url: '/products/remote-management-platform',
      },
    ],
    hash: 'shift-all-risk',
  },
  {
    type: 'content_image',
    position: false,
    bgDark: true,
    image: {
      sourceUrl: '/images/home/take-home-assessment-bg.svg',
      altText: 'Graphic representation of female engineer working remotely',
    },
    title: 'Don’t risk big projects & deadlines. Get full-time support',
    text: [
      {
        strong: 'Discover remote-ready rockstars that become true teammates',
        normal:
          '– Our career-conscious candidates will be fully invested in your projects from day one.',
      },
      {
        strong: 'Add geographic diversity without sacrificing productivity or collaboration',
        normal:
          '– Time-zone-aligned hires will overlap with your local team, from 3 to 8 hours a day.',
      },
      {
        strong: 'Make a hire that compliments your culture',
        normal:
          '– Our rigorous vetting process ensures great, high English proficiency candidates, but ultimately you decide whom to hire.',
      },
    ],
    hash: 'full-time-support',
  },
];

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

// TODO [TERM-512]: this page is not being used, we keep it for crawlers reference, delete in the future (in 3 months)
export default function TheTerminalDifference() {
  const router = useRouter();

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
        <link
          rel="canonical"
          href="https://www.terminal.io/why-work-with-terminal/the-terminal-difference"
          key="canonical"
        />
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        <Box bgColor="blue.100" as="section" pt={{ base: 10, xl: 20 }} pb={{ base: 20, xl: 20 }}>
          <Hero hero={hero} />
        </Box>
        <Grid as="section" gap={{ base: 14, lg: 6 }} mt={{ base: 14, lg: 28 }}>
          {masonryCards.map((card) => (
            <GridItem key={card.title} id={card.hash}>
              <MasonryCard card={card} />
            </GridItem>
          ))}
        </Grid>
        <SquarePattern mt={{ base: '-18.75rem', lg: '-14.5rem' }} />
      </Layout>
    </>
  );
}
